import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DialogActions, IconButton, MenuItem } from "@mui/material";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { makeStyles } from "@mui/styles";
import iconEdit from "assets/img/iconEdit.svg";
import avatar from "assets/img/profile-placeholder.png";
import { Form, Formik } from "formik";
import * as yup from 'yup';
import Button from "components/CustomButtons/Button";
import Dialog from "components/Dialog/Dialog";
import { actions as ManagerActions, selectors as ManagerSelectors } from "store/modules/managers";
import { actions as MetaActions } from "store/modules/meta";
import { useTranslation } from "react-i18next";
import { supportedAvatarFiles } from "utils/constant";
import { isValidFile } from "utils/fileUtils";

const useStyles = makeStyles(() => ({
  countryCode: {
    width: '100%',
    '& .MuiInputBase-root': {
      borderRadius: '4px 0 0 4px'
    },
  },
  mobilePhone: {
    '& .MuiInputBase-root': {
      borderRadius: '0 4px 4px 0'
    },
  },
  avatarImg: {
    margin: '10px auto',
    width: '100px',
    position: 'relative',
    '& img': {
      borderRadius: '100px',
      border: '2px solid #c4c4c4'
    },
    '& label': {
      position: 'absolute',
      right: '6px',
      background: '#ffffff',
      bottom: '0',
      padding: '0px',
      '& img': {
        padding: '2px',
      },
    }
  }
}));
function EditManagerDialog(props) {
  const { open, onClose, onCancel, onConfirm, data } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const isLoading = useSelector(ManagerSelectors.getIsAddingManager)
  const [profileURL, setProfileURL] = useState();
  const handleProfileFile = file => {
    const response = isValidFile(file);

    if (response.isValid) {
      const item = new Blob([file], { type: file.type });
      item.name = `profile_${file.name}`;
      const prevProfile = profileURL;
      setProfileURL(URL.createObjectURL(item))
      dispatch(ManagerActions.uploadManagerProfileImage(data.manager.id, item)).then(res => {
        if (res && res?.message) setProfileURL(prevProfile)
      });
    } else {
      dispatch(MetaActions.errorToast(response.message));
    }
  }
  useEffect(() => {
    setProfileURL(data.manager.signedProfileImageUrl);
  }, [data])
  return (
    <Dialog
      open={open}
      title={t("page_manager_dialog_edit_title")}
      onClose={onClose}
      onCancel={onCancel}
      alertType={'form'}
      hideActions
      size="sm"
      modalType="scrollable"
    >
      <Grid item xs={12} style={{ width: "100%", paddingLeft: 0, paddingRight: 0 }}>
        <Formik
          initialValues={{
            managerId: data.managerId,
            firstName: data.manager.firstName,
            lastName: data.manager.lastName,
            title: data.title,
            email: data.manager.email,
            mobilePhone: data.manager.mobilePhone,
            countryCode: '+1'
          }}
          onSubmit={(values, actions) => {
            onConfirm(values);
            actions.setSubmitting(false);
          }}
          validationSchema={yup.object().shape({
            email: yup.string().email(t('common_errors_enter_valid_email')).required(t("common_errors_required")).max(100,t("common_errors_max_email_length",{length:100})),
            // title: yup.string().required(t("common_errors_required")),
            firstName: yup.string().required(t("common_errors_required")).max(20,t("common_errors_max_first_name_length",{length:20})),
            lastName: yup.string().required(t("common_errors_required")).max(20, t("common_errors_max_last_name_length",{length:20})),
            title: yup.string().required(t("common_errors_required")).max(20, t("common_errors_max_title_length",{length:20})),
            // mobilePhone: yup.string().max(10, 'Please enter valid phone').min(10, 'Please enter valid phone').required(t("common_errors_required")),
            countryCode: yup.string().required(t("common_errors_required")),
          })}>
          {props => {
            const {
              values,
              touched,
              errors,
              handleChange,
              handleBlur,
            } = props;

            const [mobilePhone, setMobilePhone] = useState(values.mobilePhone);
            const [changedMobilePhone, setChangedMobilePhone] = useState(false);

            const handleMobilePhoneChange = (e) => {
              if (e.target.value.match(/[0-9]/g)) {
                if (e.target.value.length <= 10) {
                  setMobilePhone(e.target.value)
                  values.mobilePhone = e.target.value
                  if (values.mobilePhone.length === 10) {
                    setChangedMobilePhone(true)
                  }
                  else {
                    setChangedMobilePhone(false)
                  }
                }
              }
              else {
                setMobilePhone("")
                setChangedMobilePhone(false)
              }
            }

            return (
              <Form>
                <Grid
                  item
                  container
                  direction="row"
                  xs={12}
                >
                  <Grid container item xs={12} md={12} spacing={1}>
                    <div className={classes.avatarImg}>
                      <img width={'100'} height={'100'} src={profileURL || avatar} onError={(e) => { e.target.onerror = null; e.target.src = avatar }} alt="..." />
                      <IconButton className="edit-icon" disableRipple component="label">
                        <img src={iconEdit} />
                        <input type="file" accept={supportedAvatarFiles.map(a => a.mime).join(",")} name="profileImage" hidden onChange={files => {
                          handleProfileFile(files.target.files[0])
                          // setFieldValue("profileImage", files.currentTarget.files[0])
                        }} />
                      </IconButton>
                    </div>
                  </Grid>
                  <Grid container item xs={12} md={12} spacing={1}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        id="firstName"
                        name="firstName"
                        label={t("common_labels_first_name")}
                        className={classes.textField}
                        value={values.firstName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        error={errors.firstName && touched.firstName}
                        helperText={errors.firstName && touched.firstName && errors.firstName}
                        margin="normal"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        id="lastName"
                        name="lastName"
                        label={t("common_labels_last_name")}
                        className={classes.textField}
                        value={values.lastName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        fullWidth
                        error={errors.lastName && touched.lastName}
                        helperText={errors.lastName && touched.lastName && errors.lastName}
                        margin="normal"
                      />
                    </Grid>

                  </Grid>
                  <Grid item xs={12} md={12} >
                    <TextField
                      id="title"
                      name="title"
                      label={t("common_labels_title")}
                      className={classes.textField}
                      value={values.title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      error={errors.title && touched.title}
                      helperText={errors.title && touched.title && errors.title}
                      margin="normal"
                    />
                  </Grid>
                  <Grid container item xs={12} md={12}>
                    <TextField
                      id="email"
                      name="email"
                      label={t("common_labels_email")}
                      className={classes.textField}
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      error={errors.email && touched.email}
                      helperText={errors.email && touched.email && errors.email}
                      margin="normal"
                      disabled
                    />
                  </Grid>
                  <Grid container direction="row" item xs={12} sm={12}>
                    <Grid container item xs={4} sm={2}>
                      <TextField
                        select
                        name="countryCode"
                        label={t("common_labels_code")}
                        className={classes.countryCode}
                        value={values.countryCode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled
                        helperText={
                          errors.countryCode && touched.countryCode && errors.countryCode
                        }
                        margin="normal"
                      >
                        <MenuItem value={'+1'}>
                          +1
                        </MenuItem>
                      </TextField>
                    </Grid>
                    <Grid container item xs={8} sm={10}>
                      <TextField
                        name="mobilePhone"
                        label={t("common_labels_mobile_phone")}
                        className={classes.mobilePhone}
                        value={mobilePhone}
                        onChange={handleMobilePhoneChange}
                        fullWidth
                        type="number"
                        disabled
                        margin="normal"
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <DialogActions className={classes.footer}>
                  <Button
                    type="button"
                    className="outline"
                    onClick={onCancel}
                    color="transparent"
                    round
                  >
                    {t("common_labels_close")}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    round
                    disabled={!_.isEmpty(errors) || isLoading}
                  >
                    {t("common_labels_save")}
                  </Button>
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </Grid>
    </Dialog>
  );
}

export default EditManagerDialog;
